import React from 'react';
import { LinkSimple, FilmStrip } from '@phosphor-icons/react';

/** Parse a URL and return an embed descriptor or null. */
export const detectEmbed = (rawUrl) => {
  if (!rawUrl || typeof rawUrl !== 'string') return null;
  let url;
  try { url = new URL(rawUrl); } catch { return null; }
  const host = url.hostname.replace(/^www\./, '').toLowerCase();
  const path = url.pathname;
  // YouTube — watch?v=, youtu.be/, /embed/, /shorts/
  if (host === 'youtube.com' || host === 'm.youtube.com') {
    const v = url.searchParams.get('v');
    if (v) return { kind: 'video', src: `https://www.youtube.com/embed/${v}` };
    const m = path.match(/^\/(embed|shorts)\/([^/?]+)/);
    if (m) return { kind: 'video', src: `https://www.youtube.com/embed/${m[2]}` };
  }
  if (host === 'youtu.be') {
    const id = path.replace(/^\//, '').split('/')[0];
    if (id) return { kind: 'video', src: `https://www.youtube.com/embed/${id}` };
  }
  // Vimeo
  if (host === 'vimeo.com') {
    const m = path.match(/^\/(\d+)/);
    if (m) return { kind: 'video', src: `https://player.vimeo.com/video/${m[1]}` };
  }
  // Loom
  if (host === 'loom.com' || host.endsWith('.loom.com')) {
    const m = path.match(/^\/share\/([^/?]+)/);
    if (m) return { kind: 'video', src: `https://www.loom.com/embed/${m[1]}` };
  }
  return null;
};

/** Direct-image detection (jpg/png/gif/webp/svg) for projects whose URL itself is an image. */
const isDirectImage = (url) => /\.(png|jpe?g|gif|webp|svg)(\?|$)/i.test(url || '');

/**
 * Reusable project media renderer. Order of preference:
 *   1. Explicit thumbnail_url (always an image)
 *   2. Embeddable url (YouTube / Vimeo / Loom → iframe)
 *   3. URL itself is a direct image → render as image
 *   4. Nothing → empty state with link icon
 */
export const ProjectMedia = ({ project, className = '', aspect = '16/9' }) => {
  const thumb = project?.thumbnail_url;
  const embed = detectEmbed(project?.url);
  const directImage = !embed && isDirectImage(project?.url) ? project.url : null;

  const wrapperClass = `relative w-full bg-secondary/50 border border-border overflow-hidden ${className}`;
  const ratio = { aspectRatio: aspect };

  if (thumb) {
    return (
      <div className={wrapperClass} style={ratio} data-testid="project-media-thumb">
        <img
          src={thumb}
          alt={project?.title || 'Project thumbnail'}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </div>
    );
  }
  if (embed) {
    return (
      <div className={wrapperClass} style={ratio} data-testid="project-media-embed">
        <iframe
          src={embed.src}
          title={project?.title || 'Project media'}
          className="absolute inset-0 w-full h-full"
          loading="lazy"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }
  if (directImage) {
    return (
      <div className={wrapperClass} style={ratio} data-testid="project-media-image">
        <img
          src={directImage}
          alt={project?.title || 'Project image'}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </div>
    );
  }
  // Empty placeholder — keeps the grid tidy
  return (
    <div
      className={`${wrapperClass} flex items-center justify-center`}
      style={ratio}
      data-testid="project-media-empty"
    >
      <FilmStrip size={32} className="text-muted-foreground/50" />
    </div>
  );
};

export default ProjectMedia;
